import {
  STRIPE_CUSTOMER_IDS_CLAIMS_KEY,
  type StripeAppName,
  type StripeAccountName,
  type StripeCustomerIdsClaim,
  type StripeAddress,
} from '@inkitt/contracts-common';
import Stripe from 'stripe';

export const createNewCustomer = async ({
  stripeInstance,
  stripeAccount,
  stripeAppName,
  email,
  userId,
  metadata,
  address,
  ipAddress,
}: {
  stripeInstance: Stripe;
  stripeAccount: StripeAccountName;
  stripeAppName: StripeAppName;
  email?: string;
  userId: string;
  metadata?: Record<string, string>;
  /** Intentionally nullable to force consumers to pass this property for tax reasons */
  address: StripeAddress | null;
  ipAddress: string | null;
}): Promise<Stripe.Customer> => {
  // Create with only stable fields (userId) so a concurrent/retry call with
  // changed email, address, IP or caller-supplied metadata doesn't trigger
  // Stripe's "same idempotency key, different params" rejection. Volatile
  // fields are written via the follow-up update below, where reusing the same
  // value on retry is a no-op.
  const customer = await stripeInstance.customers.create(
    {
      metadata: { userId },
    },
    // No userId, no stable dedupe key: omitting it stops every anonymous buyer colliding on one.
    userId ? { idempotencyKey: `cus:${stripeAccount}-${stripeAppName}-${userId}` } : undefined,
  );

  const hasVolatileFields = !!email || !!address || !!ipAddress || !!metadata;
  if (!hasVolatileFields) {
    return customer;
  }

  return stripeInstance.customers.update(customer.id, {
    ...(email && { email }),
    ...(address && { address }),
    ...(ipAddress && { tax: { ip_address: ipAddress } }),
    ...(metadata && { metadata: { ...metadata, userId } }),
  });
};

export const getCustomer = async ({
  stripeInstance,
  customerId,
}: {
  stripeInstance: Stripe;
  customerId: string;
}): Promise<Stripe.Customer | undefined> => {
  const customer = await stripeInstance.customers.retrieve(customerId);
  if (customer.deleted) {
    return undefined;
  }
  return customer;
};

export const getAllCustomerIdsFromUserCustomClaims = (
  customClaims?: Record<string, unknown>,
): StripeCustomerIdsClaim | undefined => {
  return customClaims?.[STRIPE_CUSTOMER_IDS_CLAIMS_KEY] as StripeCustomerIdsClaim | undefined;
};

export const getCustomerIdFromUserCustomClaims = ({
  stripeAppName,
  stripeAccount,
  customClaims,
}: {
  stripeAppName: StripeAppName;
  stripeAccount: StripeAccountName;
  customClaims?: Record<string, unknown>;
}): string | undefined => {
  const customerIds = getAllCustomerIdsFromUserCustomClaims(customClaims);
  return customerIds?.[stripeAppName]?.[stripeAccount];
};
